/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { EvalResults, EvalTimelineEntry, EvalTimelineDisplayEntry, UserFeedbackAction, EvalResultsWithAuthoritative } from '../types/api';
import { MAX_DISPLAY_ENTRIES } from './constants';

const getEntryIdentity = (entry: EvalTimelineEntry) =>
  entry.job_id ||
  `${entry.eval_timestamp}_${entry.eval_result?.label}_${entry.eval_result?.explanation}`;

/**
 * Merges the authoritative evaluation (if any) with the latest evaluations, deduplicates entries, and classifies manual entries with user actions.
 * @param evalObj The evaluation results object to merge into a timeline.
 * @returns An array of evaluation timeline entries, merged and classified for display.
 */
export const getMergedEvalTimeline = (
  evalObj: EvalResults | undefined
): EvalTimelineDisplayEntry[] => {
  if (!evalObj) return [];

  const rawAuthoritative = (evalObj as EvalResultsWithAuthoritative).authoritative;

  const authoritative: EvalTimelineDisplayEntry | null =
    rawAuthoritative?.eval_timestamp && rawAuthoritative?.eval_result
      ? { ...rawAuthoritative, isAuthoritative: true }
      : null;

  const latestRaw: EvalTimelineDisplayEntry[] = (evalObj.latest ?? [])
    .filter((entry) => entry?.eval_timestamp && entry?.eval_result)
    .map((entry) => ({ ...(entry as EvalTimelineEntry), isAuthoritative: false }));

  const seen = new Set<string>();
  if (authoritative) {
    seen.add(getEntryIdentity(authoritative));
  }

  const latestDeduped: EvalTimelineDisplayEntry[] = [];
  latestRaw.forEach((entry) => {
    const key = getEntryIdentity(entry);
    if (seen.has(key)) return;
    seen.add(key);
    latestDeduped.push(entry);
  });

  const chronological = [
    ...(authoritative ? [authoritative] : []),
    ...latestDeduped,
  ].sort(
    (a, b) =>
      new Date(b.eval_timestamp || '').getTime() -
      new Date(a.eval_timestamp || '').getTime()
  );

  const classifyAgainst = (
    entry: EvalTimelineDisplayEntry,
    compareTo: EvalTimelineDisplayEntry | undefined
  ): UserFeedbackAction => {
    if (!compareTo) return 'create';

    const isSameLabel = entry.eval_result?.label === compareTo.eval_result?.label;
    const isSameExplanation =
      entry.eval_result?.explanation === compareTo.eval_result?.explanation;

    return isSameLabel && isSameExplanation ? 'approve' : 'edit';
  };

  chronological.forEach((entry, index) => {
    if (entry.category !== 'manual') return;

    const previousEntry = chronological[index + 1];

    if (previousEntry) {
      entry.userAction = classifyAgainst(entry, previousEntry);
      return;
    }

    const fallbackTarget =
      latestRaw[0] && getEntryIdentity(latestRaw[0]) !== getEntryIdentity(entry)
        ? latestRaw[0]
        : undefined;

    entry.userAction = classifyAgainst(entry, fallbackTarget);
  });

  const evalCount = evalObj?.summary?.eval_count;
  const oldestEntry = chronological[chronological.length - 1];

  if (
    typeof evalCount === 'number' &&
    evalCount <= 3 &&
    oldestEntry?.category === 'manual'
  ) {
    oldestEntry.userAction = 'create';
  }

  const latestSlots = MAX_DISPLAY_ENTRIES - (authoritative ? 1 : 0);

  const latestForDisplay = [...latestDeduped]
    .sort(
      (a, b) =>
        new Date(b.eval_timestamp || '').getTime() -
        new Date(a.eval_timestamp || '').getTime()
    )
    .slice(0, latestSlots);

  if (latestForDisplay[0]) {
    latestForDisplay[0].isLatestResult = true;
  }

  return [...(authoritative ? [authoritative] : []), ...latestForDisplay];
};